import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import authService from '../../services/authService';
import './Auth.css';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailFromUrl = searchParams.get('email');

  const [formData, setFormData] = useState({
    email: emailFromUrl || '',
    otp: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [timer, setTimer] = useState(0);
  const [canResend, setCanResend] = useState(true);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleSendOTP = async () => {
    if (!formData.email) {
      toast.error('Please enter your email address');
      return;
    }

    setSending(true);
    try {
      await authService.forgotPassword(formData.email);
      toast.success('OTP sent to your email');
      setOtpSent(true);
      setCanResend(false);
      setTimer(60);
    } catch (error) {
      toast.error(error.message || 'Failed to send OTP');
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email) {
      toast.error('Email is required');
      return;
    }

    if (!formData.otp || formData.otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    if (formData.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await authService.resetPassword(formData.email, formData.otp, formData.newPassword);
      toast.success('Password reset successful! Please login with your new password');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      toast.error(error.message || 'Failed to reset password. OTP may be invalid or expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      {/* Left – Branding */}
      <div className="auth-left">
        <div className="auth-brand">
          <div className="auth-brand-icon">
            <span className="material-symbols-outlined">work</span>
          </div>
          <span className="auth-brand-name">HiringOpen</span>
        </div>
        <h1 className="auth-left-headline">Create a New<br />Password</h1>
        <p className="auth-left-sub">Enter the OTP we sent you and set a strong new password to secure your account.</p>
        <div className="auth-features">
          {[
            { icon: 'lock', title: 'Secure by Default', desc: 'One-time codes keep your account safe' },
            { icon: 'shield', title: 'Strong Passwords', desc: 'Minimum 8 characters for better security' },
            { icon: 'schedule', title: 'Time-Limited OTP', desc: 'Codes expire in 10 minutes' },
            { icon: 'verified_user', title: 'Instant Recovery', desc: 'Get back to your account quickly' },
          ].map(f => (
            <div key={f.title} className="auth-feature">
              <div className="auth-feature-icon">
                <span className="material-symbols-outlined">{f.icon}</span>
              </div>
              <div className="auth-feature-text">
                <h4>{f.title}</h4>
                <p>{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right – Form */}
      <div className="auth-right">
        <Link to="/" className="auth-back-link">
          <span className="material-symbols-outlined">arrow_back</span>
          Back to Home
        </Link>
        <div className="auth-form-wrap">
          <div className="auth-form-icon">
            <span className="material-symbols-outlined">key</span>
          </div>
          <h1 className="auth-form-title">Reset Password</h1>
          <p className="auth-form-subtitle">Enter the OTP and your new password</p>

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="auth-field">
              <label className="auth-label">
                <span className="material-symbols-outlined">email</span>
                Email Address
              </label>
              <input
                type="email"
                name="email"
                className="auth-input"
                placeholder="john@example.com"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={otpSent}
              />
            </div>

            <button
              type="button"
              className="auth-otp-action"
              onClick={handleSendOTP}
              disabled={sending || !canResend || !formData.email}
            >
              {sending ? (
                <>
                  <div className="auth-spinner"></div>
                  Sending…
                </>
              ) : !canResend ? (
                <>
                  <span className="material-symbols-outlined">timer</span>
                  Resend in {timer}s
                </>
              ) : otpSent ? (
                <>
                  <span className="material-symbols-outlined">refresh</span>
                  Resend OTP
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined">send</span>
                  Send OTP
                </>
              )}
            </button>

            <div className="auth-field">
              <label className="auth-label">
                <span className="material-symbols-outlined">password</span>
                Enter OTP
              </label>
              <div className="auth-otp-input-wrap">
                <input
                  type="text"
                  name="otp"
                  className="auth-input"
                  placeholder="000000"
                  value={formData.otp}
                  onChange={handleChange}
                  maxLength={6}
                  required
                />
              </div>
              {otpSent && (
                <div className="auth-otp-hint">
                  <span className="material-symbols-outlined">info</span>
                  Check your email for the 6-digit OTP
                </div>
              )}
            </div>

            <div className="auth-field">
              <label className="auth-label">
                <span className="material-symbols-outlined">lock</span>
                New Password
              </label>
              <div className="auth-password-wrap">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="newPassword"
                  className="auth-input"
                  placeholder="Min. 8 characters"
                  value={formData.newPassword}
                  onChange={handleChange}
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <span className="material-symbols-outlined">
                    {showPassword ? 'visibility_off' : 'visibility'}
                  </span>
                </button>
              </div>
            </div>

            <div className="auth-field">
              <label className="auth-label">
                <span className="material-symbols-outlined">lock</span>
                Confirm Password
              </label>
              <div className="auth-password-wrap">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  className="auth-input"
                  placeholder="Re-enter new password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  <span className="material-symbols-outlined">
                    {showConfirmPassword ? 'visibility_off' : 'visibility'}
                  </span>
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="auth-submit-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="auth-spinner"></div>
                  Resetting…
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined">check</span>
                  Reset Password
                </>
              )}
            </button>
          </form>

          <div className="auth-bottom-link">
            Remember your password? <Link to="/login">Sign In</Link>
          </div>
          <div className="auth-bottom-link" style={{ marginTop: '12px' }}>
            Need a new token? <Link to="/forgot-password">Request Again</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
